import Alert from '@mui/material/Alert';
import Link from '@mui/material/Link';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link as RouterLink, Navigate, useLocation, useNavigate } from 'react-router-dom';

import { ApiError, unwrap } from '@/api/envelope';
import { portalLogin, portalSendCode, portalVerify } from '@/api/generated';
import { tokenStore } from '@/api/token-store';
import { landingFor } from '@/app/navigation';
import { ROUTES } from '@/app/routes';
import { isActivistStaffKind, organizationRoleFromBackend, ROLES } from '@/auth/roles';
import { useAuth } from '@/auth/useAuth';

import { AuthLayout } from './AuthLayout';
import { ChooseOrganizationForm } from './ChooseOrganizationForm';
import { CredentialsForm } from './CredentialsForm';
import { VerificationForm } from './VerificationForm';
import type { Membership } from '@/auth/auth-context';

import type { CredentialsValues, VerificationValues } from './schemas';

/**
 * Signing in takes three requests, not one: check the password, ask for a code
 * to be emailed, then exchange the code for tokens.
 *
 * One screen for all three roles. Activist Technologies staff, an
 * organization's Super Admin and an organization's Admin are the same kind of
 * account on the backend and sign in at the same endpoints; the only thing that
 * differs is the role the reply names, and whether it names an organization.
 * That is what decides where the person lands.
 *
 * The two steps live on one route rather than two, because the user id from the
 * first step is needed by the other two and the address bar is the wrong place
 * to carry it. Refreshing during the code step returns to the start, which is
 * correct - the code belongs to an attempt that no longer exists.
 */
export function SignInPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signIn, signOut } = useAuth();

  /*
   * Chose Log Out: the session ends here, on arrival, not before the
   * navigation. Ending it earlier would tear the portal down under a dirty
   * form's "Leave without saving?" - and Stay has to leave the person signed
   * in, on their page, with their edits.
   */
  const loggingOut =
    (location.state as { loggingOut?: boolean } | null)?.loggingOut === true;

  useEffect(() => {
    if (loggingOut && user) signOut();
  }, [loggingOut, user, signOut]);

  const [portalUserId, setPortalUserId] = useState<string | null>(null);
  // Set when somebody belongs to more than one organization and has to say
  // which they are opening. Everything needed to finish is held here, so the
  // choice costs no extra request.
  const [choice, setChoice] = useState<{
    memberships: Membership[];
    finish: (chosen: Membership) => void;
  } | null>(null);
  const [email, setEmail] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [resent, setResent] = useState(false);

  // Where the guard turned them away from, if that is how they got here.
  const wanted = (location.state as { from?: string } | null)?.from ?? null;

  // Carried from the invitation page after somebody sets their password, so
  // they are told it worked and do not have to retype the address they just
  // proved they own.
  const arrived = location.state as { message?: string; email?: string } | null;

  /** The backend's own wording when there is any, this screen's when there is not. */
  const describe = (error: unknown, fallback: string) =>
    (error instanceof ApiError ? error.serverMessage : null) ?? fallback;

  const credentials = useMutation({
    mutationFn: async (values: CredentialsValues) => {
      setEmail(values.email);
      // The portal endpoint issues and emails the code itself, so unlike the
      // app's flow there is no second call to make here.
      const reply = await portalLogin({ body: values, throwOnError: true });
      const id = unwrap(reply.data).portal_user_id;
      if (!id) throw new Error('The server did not say who signed in.');
      return id;
    },
    onMutate: () => setFormError(null),
    onSuccess: (id) => setPortalUserId(id),
    onError: () =>
      // Never say which of the two was wrong: that tells an attacker which
      // email addresses are registered.
      setFormError('That email address or password was not recognised.'),
  });

  const verify = useMutation({
    mutationFn: async (values: VerificationValues) => {
      if (!portalUserId) throw new Error('No sign-in attempt is in progress.');
      const reply = await portalVerify({
        body: { portal_user_id: portalUserId, code: values.code },
        throwOnError: true,
      });
      return unwrap(reply.data);
    },
    onMutate: () => setFormError(null),
    onSuccess: (data) => {
      if (!data?.access_token || !data.refresh_token) {
        setFormError('Signing in did not complete. Please try again.');
        return;
      }

      const account = data.user;
      const staff = isActivistStaffKind(account.kind);

      // Every organization they belong to, with the role held in each. An
      // unrecognised role stops the sign-in rather than landing somebody on a
      // page by guesswork - and nothing is stored, so nobody is half signed in.
      const memberships: Membership[] = [];
      for (const organization of account.organizations ?? []) {
        const role = organizationRoleFromBackend(organization.role);
        if (!role) {
          setFormError('This account has a role the portal does not recognise.');
          return;
        }
        memberships.push({
          id: organization.id,
          name: organization.name,
          slug: organization.slug,
          status: organization.status,
          role,
        });
      }

      if (!staff && memberships.length === 0) {
        setFormError('This account does not belong to any organization yet.');
        return;
      }

      const finish = (chosen: Membership | null) => {
        tokenStore.set({
          accessToken: data.access_token,
          refreshToken: data.refresh_token,
        });
        // The reply carries the account itself, so there is nothing to guess
        // between username, email and a fallback.
        const role = staff ? ROLES.ACTIVIST_STAFF : chosen!.role;
        signIn({
          id: account.id,
          name: account.full_name,
          email: account.email,
          role,
          organizations: memberships,
          organizationId: chosen?.id ?? null,
          organizationName: chosen?.name ?? null,
        });
        // Staff have no dashboard, so sending everybody to one would greet a
        // staff member with a refusal the moment they signed in.
        void navigate(wanted ?? landingFor(role), { replace: true });
      };

      // Staff belong to nowhere and go straight to the staff area. One
      // organization needs no question. Several do - and asking here rather
      // than dropping them somewhere arbitrary is the whole point, because
      // their role may differ between them.
      if (staff) return finish(null);
      if (memberships.length === 1) return finish(memberships[0]);
      setChoice({ memberships, finish: (chosen) => finish(chosen) });
    },
    onError: (error) =>
      setFormError(describe(error, 'That code was not correct. Please try again.')),
  });

  const resend = useMutation({
    mutationFn: async () => {
      if (!portalUserId) return;
      await portalSendCode({
        body: { portal_user_id: portalUserId },
        throwOnError: true,
      });
    },
    onMutate: () => {
      setFormError(null);
      setResent(false);
    },
    // A new code really was issued, but nothing on screen said so, so people
    // pressed it again, or kept typing the code that had just been retired.
    onSuccess: () => setResent(true),
    onError: () => setFormError('We could not send another code. Please try again.'),
  });

  // Somebody already signed in has no business on this screen - unless they
  // are here to be signed out, which the effect above is doing right now.
  if (user) {
    if (loggingOut) return null;
    return <Navigate to={landingFor(user.role)} replace />;
  }

  if (choice) {
    return (
      <AuthLayout
        title="Choose an organization"
        description="You work with more than one. You can switch at any time."
        error={formError}
      >
        <ChooseOrganizationForm
          memberships={choice.memberships}
          onChoose={choice.finish}
        />
      </AuthLayout>
    );
  }

  if (portalUserId) {
    return (
      <AuthLayout
        title="Check your email"
        description={`We sent a four-digit code to ${email}.`}
        error={formError}
      >
        <VerificationForm
          onSubmit={(values) => verify.mutate(values)}
          onResend={() => resend.mutate()}
          pending={verify.isPending}
          resending={resend.isPending}
          resent={resent}
        />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Sign in"
      description="Manage your organization's presence in ActivistARC."
      error={formError}
      footer={
        <Link component={RouterLink} to={ROUTES.forgotPassword} variant="body2">
          Forgotten your password?
        </Link>
      }
    >
      {arrived?.message ? (
        <Alert severity="success" role="status" sx={{ mb: 2 }}>
          {arrived.message}
        </Alert>
      ) : null}

      <CredentialsForm
        onSubmit={(values) => credentials.mutate(values)}
        pending={credentials.isPending}
        defaultEmail={arrived?.email}
      />
    </AuthLayout>
  );
}
